import type { GlobalThemeOverrides } from "naive-ui";
import { merge } from "lodash-es";
import { defineStore } from "pinia";

interface IThemeStore {
  themeOverrides: GlobalThemeOverrides;
}

export const useThemeStore = defineStore("theme", () => {
  const store: IThemeStore = reactive({
    themeOverrides: {
      common: {
        heightLarge: "48px",
        primaryColor: "#005738",
        primaryColorHover: "#00734a",
        primaryColorPressed: "#004d32",
        primaryColorActive: "#005738",
        textColorBase: "#222222",
        errorColor: "#DB3143",
        borderRadius: "4px",
        borderRadiusSmall: "4px",
        heightHuge: "48px",
        heightMedium: "40px",
        heightSmall: "32px",
      },
      Menu: {
        itemColorActive: "#005738",
        itemColorActiveHover: "#005738",
        itemTextColor: "#222222",
        itemIconColor: "#222222",
        itemTextColorActive: "#ECF3F1",
        itemTextColorActiveHover: "#ECF3F1",
        itemIconColorActive: "#ECF3F1",
        itemIconColorActiveHover: "#ECF3F1",
        itemHeight: "36px",
      },
      Breadcrumb: {
        itemColor: "#7F8F9B",
        itemTextColorActive: "#005738",
        itemColorHover: "#005738",
        fontSize: "14px",
      },
      Form: {
        feedbackHeightLarge: "16px",
        feedbackHeightSmall: "16px",
        feedbackHeightMedium: "16px",
        labelPaddingVertical: "0 0 11px 2px",
        labelPaddingHorizontal: "0 14px 0 0",
        feedbackFontSizeMedium: "12px",
        labelFontSizeTopSmall: "14px",
        blankHeightSmall: "32px",
        blankHeightMedium: "32px",
        labelTextColor: "#222222",
        feedbackPadding: "0 0 0 2px",
      },
      Button: {
        fontSizeTiny: "10px",
        paddingTiny: "4px 12px",
        borderRadiusSmall: "6px",
        paddingSmall: "6px 12px",
        textColorTextDisabledError: "#FA4040",
        textColorTextError: "#FA4040",
        textColorError: "#FA4040",
        textColorGhostError: "#FA4040",
        borderError: "1px solid #FA4040",
        rippleColorError: "#FA4040",
        iconMarginSmall: "6px 4px 6px 0",
        borderRadiusLarge: "6px",
        heightLarge: "48px",

        textColorTextDisabled: "#222",
        textColorText: "#222",
        textColor: "#222",
        textColorGhost: "#222",
        border: "1px solid #555",
        rippleColor: "#222",
        iconSizeSmall: "16px",
      },
      Input: {
        borderRadius: "4px",
        suffixTextColor: "#888888",
        fontSizeSmall: "14px",
        colorDisabled: "#f5f5f5",
        textColor: "#222222",
        textColorDisabled: "#AAA",
        heightLarge: "48px",
        paddingSmall: "5px 8px",
        borderError: "1px solid #FA4040",
        placeholderColor: "#AAA",
        placeholderColorDisabled: "#AAA",
      },
      InternalSelection: {
        colorDisabled: "#f5f5f5",
        textColor: "#222222",
        paddingSingle: "0  26px 0 8px",
        placeholderColor: "#AAA",
        placeholderColorDisabled: "#AAA",
      },
      Tag: {
        borderRadius: "4px",
        textColorError: "#FA4040",
        border: "rgba(136, 136, 136, 0.10)",
        colorBordered: "rgba(136, 136, 136, 0.10)",
        colorBorderedError: "#FFE6D9",
        textColorSuccess: "#005738",
        colorBorderedSuccess: "#E1EAE7",
        textColorWarning: "#FF6600",
        colorBorderedWarning: "#FBEBE1",
        textColorInfo: "#555555",
        colorBorderedInfo: "#F3F3F3",
        fontSizeSmall: "13px",
        padding: "1px 4px",
        heightSmall: "20px",
      },
      DataTable: {
        tdTextColor: "#222222",
        thTextColor: "#222222",
        thColor: "#F2F2F2",
        thPaddingMedium: "13px 8px",
        tdColorStriped: "#FAFAFA",
        tdPaddingMedium: "8px 8px",
        borderColor: "#EEEEEE",
        tdColorHover: "#E5EEEB",
        borderRadius: 0,
      },
      Dialog: {
        titleFontSize: "16px",
        titleTextColor: "#222222",
        iconSize: "24px",
        padding: "0",
        borderRadius: "4px",
        closeMargin: "16px 26px",
        contentMargin: "0",
        actionSpace: "8px",
        closeIconColor: "#222222",
        closeIconSize: "0",
      },
      Checkbox: {
        textColor: "#222222",
        labelPadding: "0 4px",
      },
      Switch: {
        buttonWidthSmall: "11px",
        buttonHeightSmall: "11px",
        railHeightSmall: "16px",
        railWidthSmall: "30px",
      },
      Popover: {
        padding: "4px 8px",
        color: "#ffffff",
        textColor: "#222222",
      },
      Drawer: {
        headerPadding: "0",
        bodyPadding: "0",
      },
      Pagination: {
        itemSizeMedium: "32px",
        itemColorActive: "#005738",
        itemColorActiveHover: "#005738",
        itemTextColorActive: "white",
        itemBorderRadius: "6px",
        itemBorder: "1px solid #ebebeb",
        itemBorderHover: "1px solid #ebebeb",
      },
      Radio: {
        dotColorActive: "#ffffff",
        colorActive: "#005738",
        boxShadowActive: "0 0 0 1px #C3E5CB",
        labelPadding: "0 2px",
      },
    },
  });

  function setThemeOverrides(theme: GlobalThemeOverrides) {
    store.themeOverrides = merge(store.themeOverrides, theme);
  }

  return {
    ...toRefs(store),
    setThemeOverrides,
  };
});
